import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Grid
} from '@mui/material';
import {
  LocalShipping,
  CheckCircle,
  Warning,
  Schedule,
  OpenInNew,
  LocationOn,
  CalendarToday,
  Info
} from '@mui/icons-material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import { shipmentService } from '../../services/shipment.service';

const CarrierTrackingDialog = ({ open, onClose, carrierId, trackingNumber, carrierName }) => {
  const [dialogState, setDialogState] = useState({
    loading: false,
    error: null,
    trackingInfo: null,
    trackingUrl: null
  });

  // Log props and state for debugging
  console.log("CarrierTrackingDialog props:", { open, carrierId, trackingNumber, carrierName });
  console.log("CarrierTrackingDialog state:", dialogState);

  useEffect(() => {
    // Only fetch data if dialog is open and we have carrier ID and tracking number
    if (open && carrierId && trackingNumber) {
      fetchTrackingInfo();
    } else if (!open) {
      // Reset state when dialog closes
      setDialogState(prev => ({
        ...prev,
        loading: false,
        error: null
      }));
    }
  }, [open, carrierId, trackingNumber]);

  const fetchTrackingInfo = async () => {
    try {
      console.log(`Fetching tracking info for carrier: ${carrierId}, tracking: ${trackingNumber}`);
      setDialogState(prev => ({ ...prev, loading: true, error: null }));

      // Fetch tracking information
      const trackingResponse = await shipmentService.getTrackingInfo(carrierId, trackingNumber);
      console.log("Tracking API response:", trackingResponse);

      // Fetch tracking URL (if available)
      let trackingUrl = null;
      try {
        const urlResponse = await shipmentService.getTrackingUrl(carrierId, trackingNumber);
        trackingUrl = urlResponse.data?.url || null;
        console.log("Tracking URL response:", urlResponse);
      } catch (urlError) {
        console.warn("Could not fetch tracking URL:", urlError);
      }

      // Extract tracking data from response
      const trackingData = trackingResponse.data || trackingResponse;
      
      setDialogState({
        loading: false,
        error: null,
        trackingInfo: trackingData,
        trackingUrl: trackingUrl
      });
    } catch (error) {
      console.error("Error fetching tracking information:", error);
      setDialogState({
        loading: false,
        error: "Failed to load tracking information. Please try again later.",
        trackingInfo: null,
        trackingUrl: null
      });
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    if (!status) return 'default';
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes('deliver') || statusLower.includes('complete')) {
      return 'success';
    } else if (statusLower.includes('transit') || statusLower.includes('pickup')) {
      return 'primary';
    } else if (statusLower.includes('delay') || statusLower.includes('exception')) {
      return 'warning';
    } else if (statusLower.includes('fail') || statusLower.includes('cancel')) {
      return 'error';
    }
    return 'default';
  };

  // Get status icon
  const getStatusIcon = (status) => {
    if (!status) return <Info />;
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes('deliver') || statusLower.includes('complete')) {
      return <CheckCircle color="success" />;
    } else if (statusLower.includes('transit') || statusLower.includes('pickup')) {
      return <LocalShipping color="primary" />;
    } else if (statusLower.includes('delay') || statusLower.includes('exception')) {
      return <Warning color="warning" />;
    } else if (statusLower.includes('fail') || statusLower.includes('cancel')) {
      return <Warning color="error" />;
    }
    return <Schedule />;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="tracking-dialog-title"
    >
      <DialogTitle id="tracking-dialog-title">
        {carrierName || 'Carrier'} Tracking Information
      </DialogTitle>
      
      <DialogContent dividers>
        {dialogState.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : dialogState.error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {dialogState.error}
          </Alert>
        ) : dialogState.trackingInfo ? (
          <Box>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Shipment Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Tracking Number</Typography>
                  <Typography variant="body1">{trackingNumber}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Carrier</Typography>
                  <Typography variant="body1">{carrierName || carrierId}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Chip 
                    label={dialogState.trackingInfo.status || 'Unknown'} 
                    color={getStatusColor(dialogState.trackingInfo.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Estimated Delivery</Typography>
                  <Typography variant="body1">
                    {formatDate(dialogState.trackingInfo.estimated_delivery)}
                  </Typography>
                </Grid>
              </Grid>
              
              {dialogState.trackingUrl && (
                <Box sx={{ mt: 2 }}>
                  <Link 
                    href={dialogState.trackingUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    View on {carrierName || 'carrier'} website <OpenInNew sx={{ ml: 0.5, fontSize: '0.9rem' }} />
                  </Link>
                </Box>
              )}
            </Paper>
            
            {dialogState.trackingInfo.tracking_history && dialogState.trackingInfo.tracking_history.length > 0 ? (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Tracking History
                </Typography>
                <List>
                  {dialogState.trackingInfo.tracking_history.map((event, index) => (
                    <React.Fragment key={`tracking-event-${index}`}>
                      {index > 0 && <Divider component="li" />}
                      <ListItem alignItems="flex-start">
                        <ListItemIcon>
                          {getStatusIcon(event.status)}
                        </ListItemIcon>
                        <ListItemText
                          primary={event.status}
                          secondary={
                            <React.Fragment>
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.primary"
                              >
                                {formatDate(event.timestamp)}
                              </Typography>
                              {event.location && (
                                <Typography component="div" variant="body2">
                                  {event.location}
                                </Typography>
                              )}
                              {event.description && (
                                <Typography component="div" variant="body2">
                                  {event.description}
                                </Typography>
                              )}
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            ) : (
              <Alert severity="info">
                No tracking history available for this shipment.
              </Alert>
            )}
          </Box>
        ) : (
          <Alert severity="info">
            No tracking information available for this shipment.
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CarrierTrackingDialog; 